import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { LookupOption, LookupResult } from './aiAssistantApi'
import { isCreatableLookupEntity, lookupEntityNoun } from './formRequestModel'

type Props = {
  id: string
  // The stored value: the chosen record's id, or '' / null when none.
  value: unknown
  // Label to display for a value that is already set (a prefilled default, or
  // a rehydrated selection whose label wasn't kept in the form value).
  defaultLabel?: string
  placeholder?: string
  disabled: boolean
  // Shown beneath a disabled control when it's blocked waiting on a parent
  // field (e.g. "Choose a customer first").
  hint?: string
  parentValue?: string | number | null
  error?: string
  style: CSSProperties
  // The kind of record searched, from the field spec. Decides whether the
  // no-matches state can offer to create one.
  entity?: string | null
  onSearch: (
    query: string,
    parentValue: string | number | null
  ) => Promise<LookupResult>
  onChange: (id: unknown) => void
  onBlur: () => void
  // Asks the assistant to create a record by the name the user typed. Omitted
  // where no create path exists (a read-only replay), which keeps the plain
  // no-matches text.
  onCreateRequest?: (query: string) => void
}

const SEARCH_DEBOUNCE_MS = 300

/**
 * A typeahead for a `lookup` field: the user searches records by name and picks
 * one, and the field submits that record's id. The chosen label is shown as a
 * chip with a clear affordance; the form value holds only the id. Search runs
 * against the chat lookup endpoint via `onSearch`, debounced, with stale
 * responses ignored. A dependent lookup is disabled (with `hint`) until its
 * parent is chosen — that gating is decided by the caller.
 */
const FormLookupInput = ({
  id,
  value,
  defaultLabel,
  placeholder,
  disabled,
  hint,
  parentValue,
  error,
  style,
  entity,
  onSearch,
  onChange,
  onBlur,
  onCreateRequest,
}: Props) => {
  const [query, setQuery] = useState('')
  const [selectedLabel, setSelectedLabel] = useState<string | null>(
    defaultLabel ?? null
  )
  const [results, setResults] = useState<LookupOption[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  // Ignore responses from searches superseded by a newer keystroke.
  const seqRef = useRef(0)

  // The parent rebuilds `onSearch` on every render (it closes over the session
  // and form identity). Hold it in a ref so the search effect doesn't depend on
  // its identity — otherwise any ancestor re-render while the dropdown is open
  // (an SSE token, a sibling field changing) would re-fire the search.
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  const hasSelection = value !== '' && value != null

  // What the no-matches state can offer. A create needs a name to create the
  // record under, so it waits for the user to type something; an entity the
  // assistant has no create tool for is told where its records come from
  // instead, rather than offered an action that cannot work.
  const trimmedQuery = query.trim()
  const entityNoun = entity ? lookupEntityNoun(entity) : ''
  const creatable = isCreatableLookupEntity(entity)
  const canCreate = creatable && !!onCreateRequest && trimmedQuery !== ''
  const isSetupData = !!entity && !creatable

  // When the value is cleared from outside (a parent-field change cascades a
  // reset), drop any shown selection so the search input reappears.
  useEffect(() => {
    if (!hasSelection) {
      setSelectedLabel(null)
      setQuery('')
      setOpen(false)
    }
  }, [hasSelection])

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++seqRef.current
      setLoading(true)
      try {
        const res = await onSearchRef.current(q, parentValue ?? null)
        if (seqRef.current === seq) {
          setResults(res.options)
          setTotalCount(res.total_count)
        }
      } catch {
        if (seqRef.current === seq) {
          setResults([])
          setTotalCount(0)
        }
      } finally {
        if (seqRef.current === seq) {
          setLoading(false)
        }
      }
    },
    [parentValue]
  )

  // Search when the dropdown is open: immediately on open (empty query =
  // match-all), debounced as the user types.
  useEffect(() => {
    if (!open) {
      return
    }
    const delay = query === '' ? 0 : SEARCH_DEBOUNCE_MS
    const timer = setTimeout(() => runSearch(query), delay)
    return () => clearTimeout(timer)
  }, [open, query, runSearch])

  const handleSelect = (option: LookupOption) => {
    setSelectedLabel(option.label)
    setQuery('')
    setResults([])
    setOpen(false)
    onChange(option.id)
    onBlur()
  }

  const handleClear = () => {
    setSelectedLabel(null)
    setQuery('')
    onChange('')
  }

  if (hasSelection) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          ...style,
          padding: '5px 8px',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedLabel ?? String(value)}
        </span>
        {!disabled && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={handleClear}
            style={{
              border: 'none',
              background: 'none',
              color: '#888',
              cursor: 'pointer',
              font: 'inherit',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so a click on an option lands before the list unmounts.
          setTimeout(() => setOpen(false), 150)
          onBlur()
        }}
        style={style}
      />
      {hint && disabled && (
        <span style={{ fontSize: 12, color: '#888' }}>{hint}</span>
      )}
      {open && !disabled && (
        <ul
          style={{
            position: 'absolute',
            zIndex: 10,
            top: '100%',
            left: 0,
            right: 0,
            margin: '2px 0 0',
            padding: 0,
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <li style={{ padding: '6px 10px', color: '#888' }}>Searching…</li>
          )}
          {!loading && results.length === 0 && (
            <li style={{ padding: '6px 10px', color: '#888' }}>
              <span>No matches</span>
              {/* A lookup only ever searches records that already exist, so
                  anything new used to dead-end here — four golden-demo reviews
                  running (#466). The create runs in chat, where it already
                  works; this is the door to it. */}
              {canCreate && (
                <button
                  type="button"
                  // Keep focus so onBlur doesn't close the list before onClick.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onCreateRequest?.(trimmedQuery)}
                  style={{
                    display: 'block',
                    marginTop: 4,
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    color: '#1677ff',
                    cursor: 'pointer',
                    font: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  Create {entityNoun} &quot;{trimmedQuery}&quot;
                </button>
              )}
              {isSetupData && (
                <span style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                  {entityNoun}s come from your company setup.
                </span>
              )}
            </li>
          )}
          {!loading &&
            results.map((option) => (
              <li key={String(option.id)}>
                <button
                  type="button"
                  // Keep focus so onBlur doesn't close the list before onClick.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(option)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  <span>{option.label}</span>
                  {option.description && (
                    <span
                      style={{ display: 'block', fontSize: 12, color: '#888' }}
                    >
                      {option.description}
                    </span>
                  )}
                </button>
              </li>
            ))}
          {!loading && totalCount > results.length && (
            <li style={{ padding: '6px 10px', fontSize: 12, color: '#888' }}>
              Showing {results.length} of {totalCount} — keep typing to narrow
            </li>
          )}
        </ul>
      )}
      {error && <span style={{ fontSize: 12, color: '#ff4d4f' }}>{error}</span>}
    </div>
  )
}

export default FormLookupInput
